import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { MemberService } from 'app/cd/services/member-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { SaveRace } from 'app/core/components/races-ethnicities/save-race';
import { I18N } from 'aurelia-i18n';
import * as CD from 'app/cd/models/essette-api';

export enum FlagOptions {
  availableAsBirthRace = 'cd:member.race.availableAsBirthRace.$',
  availableAsIdentity = 'cd:member.race.availableAsIdentity.$'
}

interface CustomRace extends CD.Race {
  flagCount?: number | null;
  flagToolTipDesc?: string | null;
}

@autoinject()
export class Races extends ComponentBase {

  ////****************** Fields and Properties ************************************////
  @bindable
  protected config: IBaseConfig = {};

  protected race: CD.Race | null = null;
  protected races: ReadonlyArray<CustomRace> | null = null;

  private sorting: Sorting = new Sorting();
  protected resultCount: number | null = 0;
  protected pageSize: number = 25;
  protected includeInactive: boolean = false;

  protected validate = validate;

  ////****************** Element References ***************************************////
  protected $pager!: EssPagerCustomElement;

  protected $saveRaceModal!: IPopup;
  protected $form!: IForm;
  protected $raceForm!: Element;
  protected $saveRace!: SaveRace;

  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly service: MemberService,
    private readonly toaster: Toaster,
    protected readonly i18n: I18N,
    ...rest: any[]) {
    super(...rest);
  }

  protected bind(): void {
    this.service.configure(this.config);
    this.reload();
  }

  ////****************** Events ***************************************************////
  protected formSuccess({ detail }: FormSuccessCustomEvent) {
    this.$saveRaceModal.close();

    if(detail.form.reset) detail.form.reset();

    // reload grid and reset page
    this.reload();
  }

  /** Handles the grid's sort event */
  protected applySort(event: SortChangeEvent): void {
    const { property, direction } = event.detail;
    const { sorting } = this;
    if(direction === null) {
      sorting.remove(property);
    } else {
      sorting.add({
        reference: property,
        direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
      });
    }
    this.reload();
  }

  ////****************** Functions ************************************************////
  public async reload(): Promise<void> {
    await this.loadRaces();
  }

  protected saveRace(race: CD.Race): void {
    this.$saveRace.race = race;
    this.race = race;

    this.$saveRaceModal.open();
  }

  protected saveRaceClose(): void {
    if(this.$form.reset) this.$form.reset();
    this.$saveRaceModal.close();
  }

  protected setRaceFlags = (races: CustomRace[]): CustomRace[] => {
    races.forEach(race => {
      let listDisplay = '<ul>';
      race.flagCount = 0;
      for(const [flag, value] of Object.entries({ ...race.flag })) {
        if(value) {
          race.flagCount++;
          listDisplay += '<li>' + this.i18n.tr('cd:member.race.' + flag + '.$') + '</li>';
        }
      }
      listDisplay += '</ul>';
      race.flagToolTipDesc = listDisplay;
    });
    return races;
  }

  ////****************** Service Calls ********************************************////
  @apiCall
  protected async activateRace(race: CD.Race): Promise<void> {
    // Show confirmation dialog only if deactivating
    if(race.isActive) {
      const result = await confirmation.showDeactivate('cd:member.race.$', 1);
      if(result.dialogResponse === confirmation.options.CANCEL) { return; }

      if(!(result.dialogResponse === confirmation.options.OK)) {
        return;
      }
    }

    let isSuccessful = false;
    try {
      this.state.beginSave();
      const response = await this.service.activateRace(race.id!, !race.isActive);
      isSuccessful = response.isSuccessful;
      this.reload();
    } catch(error) {
      throw error;
    } finally {
      this.toaster.standard.activate('cd:member.race.$', !race.isActive, isSuccessful);
      this.state.endSave();
    }
  }

  @apiCall
  private async loadRaces(): Promise<void> {

    const options: Partial<IQueryLoadOptions> = {
      page: this.$pager?.page || 0,
      top: this.pageSize,
      sorting: this.sorting,
      includeInactive: this.includeInactive
    };

    const includes: string[] = [];

    try {
      this.state.beginLoad();

      const response = await this.service.loadRaces(options, includes);

      this.races = this.setRaceFlags(response.results);
      this.resultCount = response.count;
    } catch(error) {
      throw error;
    } finally {
      this.state.endLoad();
    }
  }
}
